import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card } from '../../../components/ui/Card';
import AccountSwitcher from '../../../components/AccountSwitcher';
import { useAccounts } from '../../../context/AccountContext';
import {
  Eye, BarChart, BarChart3, ShoppingCart, TrendingUp,
  ChevronRight, ChevronDown, RefreshCw, Loader2, AlertCircle,
  Image as ImageIcon, Type, Play, MousePointerClick, ExternalLink, Info,
} from 'lucide-react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const API_BASE = 'https://api.glowytics.com';

/**
 * Performance Max — Asset Performance
 *
 * Meta Creative Performance sayfasinin PMax karsiligi. Fark: Google, PMax'te
 * asset BASINA metrik VERMEZ (`asset_group_asset` uzerinden metrics.impressions
 * secilebiliyor ama her zaman 0 doner). Bu yuzden harcama/ROAS asset group
 * satirinda; asset kartlari tur, boyut ve onay durumu tasiyor.
 *
 * Veri tamamen CANLI: her yuklemede backend Google Ads API'ye gider
 * (`/api/google-ads/live/pmax-assets`), DB'ye hic dokunmaz.
 */

// ── Asset field type'lari gorsel/metin/video kovalarina ayir ────────────────
const IMAGE_FIELDS = new Set([
  'MARKETING_IMAGE', 'SQUARE_MARKETING_IMAGE', 'PORTRAIT_MARKETING_IMAGE',
  'TALL_PORTRAIT_MARKETING_IMAGE', 'LANDSCAPE_LOGO', 'LOGO',
]);
const TEXT_FIELDS = new Set(['HEADLINE', 'LONG_HEADLINE', 'DESCRIPTION', 'BUSINESS_NAME']);
const VIDEO_FIELDS = new Set(['YOUTUBE_VIDEO']);

const FIELD_LABELS = {
  HEADLINE: 'Headline',
  LONG_HEADLINE: 'Long Headline',
  DESCRIPTION: 'Description',
  BUSINESS_NAME: 'Business Name',
  MARKETING_IMAGE: 'Landscape Image',
  SQUARE_MARKETING_IMAGE: 'Square Image',
  PORTRAIT_MARKETING_IMAGE: 'Portrait Image',
  TALL_PORTRAIT_MARKETING_IMAGE: 'Tall Portrait Image',
  LANDSCAPE_LOGO: 'Landscape Logo',
  LOGO: 'Logo',
  YOUTUBE_VIDEO: 'YouTube Video',
  CALL_TO_ACTION_SELECTION: 'Call To Action',
};

const STATUS_STYLES = {
  ELIGIBLE: 'bg-emerald-50 text-emerald-700',
  ENABLED: 'bg-emerald-50 text-emerald-700',
  PAUSED: 'bg-amber-50 text-amber-700',
  REMOVED: 'bg-gray-100 text-gray-500',
  LIMITED: 'bg-amber-50 text-amber-700',
  NOT_ELIGIBLE: 'bg-rose-50 text-rose-700',
  PENDING: 'bg-blue-50 text-blue-700',
};

// ── Metrik katmani: Meta Creative Performance ile BIREBIR ayni ──────────────
// Ayni fonksiyon govdesi, ayni kolon seti (Spend / Revenue / Conv. / Clicks /
// Impr. / CTR / CPM / ROAS), ayni bicimlendirme. Iki sayfa yan yana karsilastirilabilsin
// diye kasitli olarak kopyalandi — burada bir seyi "iyilestirirken" Meta tarafini
// da guncelle, yoksa sayilar ayrisir.
const formatLargeNumber = (num) => {
  if (num === undefined || num === null) return '0';
  if (num === 0) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
};

// KPI kartindaki rozet — Meta'da "Last 30d" sabit yazili; burada secilen araligi
// yansitiyor ki 7 gun secildiginde kart yanlis etiket tasimasin.
const DATE_BADGE = {
  today: 'Today', yesterday: 'Yesterday', last7: 'Last 7d', last30: 'Last 30d',
  last90: 'Last 90d', thisMonth: 'This month', lastMonth: 'Last month',
};

const money = (n) => `₺${Math.round(Number(n || 0)).toLocaleString('tr-TR')}`;
// Meta'da satin alma sayisi tam sayidir; Google donusumleri ondalikli gelir
// (38.24 gibi). Ayni gorunumu vermek icin gosterimde yuvarliyoruz.
const count = (n) => Math.round(Number(n || 0)).toLocaleString('tr-TR');
const cpmOf = (spend, impressions) =>
  Number(impressions || 0) > 0 ? (Number(spend || 0) / Number(impressions || 0)) * 1000 : 0;
// Meta'da CTR creative kartinda duruyor (orada metrik creative seviyesinde var).
// PMax'te asset seviyesinde metrik olmadigi icin ayni formul asset group satirinda.
const ctrOf = (clicks, impressions) =>
  Number(impressions || 0) > 0 ? (Number(clicks || 0) / Number(impressions || 0)) * 100 : 0;
const roasOf = (spend, revenue) =>
  Number(spend || 0) > 0 ? Number(revenue || 0) / Number(spend || 0) : 0;

export default function PMaxAssetPerformance() {
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('GOOGLE_ADS');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [fetchedAt, setFetchedAt] = useState(null);

  const [dateRange, setDateRange] = useState('last30');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);

  const [expanded, setExpanded] = useState({});           // campaignId -> bool
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [includeRemoved, setIncludeRemoved] = useState(false);
  const [sort, setSort] = useState({ key: 'spend', dir: 'desc' });

  const loadData = useCallback(async () => {
    // custom secili ama tarihler henuz girilmemisse bosa istek atma
    if (dateRange === 'custom' && (!customStartDate || !customEndDate)) return;
    setLoading(true);
    setError(null);
    try {
      const brandId = localStorage.getItem('currentBrandId');
      const params = { dateRange };
      if (dateRange === 'custom') {
        params.startDate = customStartDate.toISOString().slice(0, 10);
        params.endDate = customEndDate.toISOString().slice(0, 10);
      }
      if (selectedAccountId && selectedAccountId !== 'all') params.accountId = selectedAccountId;
      if (includeRemoved) params.includeRemoved = '1';

      const resp = await axios.get(`${API_BASE}/api/google-ads/live/pmax-assets`, {
        params,
        withCredentials: true,
        headers: { 'X-Brand-ID': brandId },
      });
      setData(resp.data);
      setFetchedAt(new Date());

      // Ilk kampanyayi acip ilk asset group'u sec — sayfa bos acilmasin.
      const first = resp.data?.campaigns?.[0];
      if (first) {
        setExpanded({ [first.id]: true });
        setSelectedGroupId(first.assetGroups?.[0]?.id || null);
      } else {
        setSelectedGroupId(null);
      }
    } catch (e) {
      const msg = e?.response?.data?.error || e.message || 'Failed to load data';
      setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dateRange, customStartDate, customEndDate, selectedAccountId, includeRemoved]);

  useEffect(() => { loadData(); }, [loadData]);

  const campaigns = data?.campaigns || [];
  const totals = data?.totals || { cost: 0, impressions: 0, clicks: 0, conversions: 0, conversionValue: 0, roas: 0 };

  const allGroups = useMemo(
    () => campaigns.flatMap(c => (c.assetGroups || []).map(g => ({ ...g, campaignName: c.name }))),
    [campaigns]
  );
  const selectedGroup = useMemo(
    () => allGroups.find(g => g.id === selectedGroupId) || null,
    [allGroups, selectedGroupId]
  );

  const bucketed = useMemo(() => {
    const assets = selectedGroup?.assets || [];
    const images = assets.filter(a => IMAGE_FIELDS.has(a.fieldType));
    const videos = assets.filter(a => VIDEO_FIELDS.has(a.fieldType));
    const texts = assets.filter(a => TEXT_FIELDS.has(a.fieldType));
    const other = assets.filter(a =>
      !IMAGE_FIELDS.has(a.fieldType) && !VIDEO_FIELDS.has(a.fieldType) && !TEXT_FIELDS.has(a.fieldType));
    // Metinleri tipe gore grupla: Basliklar, Uzun Basliklar, Aciklamalar ayri bloklar
    const textGroups = {};
    texts.forEach(a => { (textGroups[a.fieldType] = textGroups[a.fieldType] || []).push(a); });
    return { images, videos, textGroups, other, total: assets.length };
  }, [selectedGroup]);

  // ── KPI kartlari — Meta Creative Performance ile ayni 5 metrik, ayni sira ──
  // Meta: Impressions / Revenue / Spend / Conversions / ROAS
  const kpis = [
    { label: 'Impressions', value: formatLargeNumber(Math.round(totals.impressions)), icon: Eye, iconBg: 'bg-blue-50', iconFg: 'text-blue-600' },
    { label: 'Revenue', value: `₺${formatLargeNumber(Math.round(totals.conversionValue))}`, icon: BarChart, iconBg: 'bg-emerald-50', iconFg: 'text-emerald-600' },
    { label: 'Spend', value: `₺${formatLargeNumber(Math.round(totals.cost))}`, icon: BarChart3, iconBg: 'bg-indigo-50', iconFg: 'text-indigo-600' },
    { label: 'Conversions', value: formatLargeNumber(Math.round(totals.conversions)), icon: ShoppingCart, iconBg: 'bg-purple-50', iconFg: 'text-purple-600' },
    { label: 'ROAS', value: `${roasOf(totals.cost, totals.conversionValue).toFixed(2)}x`, icon: TrendingUp, iconBg: 'bg-rose-50', iconFg: 'text-rose-600' },
  ];

  // ── Siralama — Meta'daki handleCampaignSort ile ayni davranis ─────────────
  const sortedCampaigns = useMemo(() => {
    const val = (c, key) => {
      switch (key) {
        case 'spend': return Number(c.cost || 0);
        case 'revenue': return Number(c.conversionValue || 0);
        case 'purchases': return Number(c.conversions || 0);
        case 'clicks': return Number(c.clicks || 0);
        case 'impressions': return Number(c.impressions || 0);
        case 'ctr': return ctrOf(c.clicks, c.impressions);
        case 'cpm': return cpmOf(c.cost, c.impressions);
        case 'roas': return roasOf(c.cost, c.conversionValue);
        default: return 0;
      }
    };
    return [...campaigns].sort((a, b) => {
      const d = val(a, sort.key) - val(b, sort.key);
      return sort.dir === 'asc' ? d : -d;
    });
  }, [campaigns, sort]);

  const handleSort = (key) => {
    setSort(prev => (prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'desc' }));
  };

  const SortTh = ({ label, sortKey }) => (
    <th
      className="py-2 px-3 text-right cursor-pointer select-none"
      onClick={() => handleSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sort.key === sortKey && <span>{sort.dir === 'asc' ? '▲' : '▼'}</span>}
      </span>
    </th>
  );

  const accountErrors = (data?.accounts || []).filter(a => a.error);

  return (
    <div className="space-y-6">
      {/* ── Başlık ─────────────────────────────────────────────────────────── */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-50">
              <ImageIcon className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Performance Max — Asset Performance</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Inspect the assets inside your asset groups, live
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 relative">
            <AccountSwitcher platform="GOOGLE_ADS" />
            <label className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs text-gray-600 cursor-pointer hover:border-gray-300 transition-all">
              <input
                type="checkbox"
                checked={includeRemoved}
                onChange={(e) => setIncludeRemoved(e.target.checked)}
                className="rounded border-gray-300"
              />
              Removed assets
            </label>
            <select
              className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-700 focus:ring-2 focus:ring-blue-100 outline-none transition-all hover:border-gray-300"
              value={dateRange}
              onChange={(e) => {
                setDateRange(e.target.value);
                setShowCalendar(e.target.value === 'custom');
              }}
            >
              <option value="today">Bugün</option>
              <option value="yesterday">Dün</option>
              <option value="last7">Son 7 gün</option>
              <option value="last30">Son 30 gün</option>
              <option value="last90">Son 90 gün</option>
              <option value="thisMonth">Bu ay</option>
              <option value="lastMonth">Geçen ay</option>
              <option value="custom">Özel</option>
            </select>
            {dateRange === 'custom' && customStartDate && customEndDate && (
              <button
                onClick={() => setShowCalendar(true)}
                className="px-2 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
              >
                {customStartDate.toLocaleDateString('tr-TR')} – {customEndDate.toLocaleDateString('tr-TR')}
              </button>
            )}
            {showCalendar && dateRange === 'custom' && (
              <div className="absolute z-50 top-full mt-2 right-0 bg-white border rounded-xl shadow-xl p-4">
                <DatePicker
                  selected={customStartDate}
                  onChange={(dates) => {
                    const [start, end] = dates;
                    setCustomStartDate(start);
                    setCustomEndDate(end);
                    if (start && end) setShowCalendar(false);
                  }}
                  startDate={customStartDate}
                  endDate={customEndDate}
                  selectsRange
                  inline
                  maxDate={new Date()}
                />
              </div>
            )}
            <button
              onClick={loadData}
              disabled={loading}
              className="p-2 bg-white border border-gray-200 rounded-xl text-gray-600 hover:border-gray-300 hover:text-gray-900 transition-all disabled:opacity-50"
              title="Refresh live"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {fetchedAt && !loading && (
          <p className="text-xs text-gray-400 mt-3">
            Fetched live from Google Ads · {fetchedAt.toLocaleTimeString('tr-TR')}
            {data?.since && ` · ${data.since} – ${data.until}`}
          </p>
        )}
      </div>

      {/* ── Hesap hataları ─────────────────────────────────────────────────── */}
      {accountErrors.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Some accounts could not be fetched</p>
            {accountErrors.map(a => (
              <p key={a.accountId} className="text-xs mt-1">
                {a.accountName || a.accountId}: {a.error}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* ── KPI kartları ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {kpis.map((m) => (
          <Card key={m.label} className="p-5 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className={`p-2.5 rounded-xl ${m.iconBg}`}>
                  <m.icon className={`w-5 h-5 ${m.iconFg}`} />
                </div>
                <span className="text-xs font-medium text-gray-400 bg-gray-50 px-2 py-1 rounded-full">
                  {DATE_BADGE[dateRange] || 'Custom'}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 mb-1">{m.label}</p>
                <p className="text-2xl font-bold text-gray-900">{loading ? '—' : m.value}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* ── Hata ───────────────────────────────────────────────────────────── */}
      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-rose-800">Failed to load data</p>
            <p className="text-xs text-rose-700 mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* ── Ana düzen ──────────────────────────────────────────────────────── */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sol: kampanya → asset group ağacı */}
        <div className="w-full lg:w-7/12">
          <Card className="rounded-xl overflow-hidden">
            <div className="p-4 border-b bg-white flex justify-between items-center">
              <span className="font-medium text-gray-800">Campaigns</span>
              <span className="text-sm text-gray-500">
                Total: {campaigns.length} PMax · {allGroups.length} asset groups
              </span>
            </div>

            {loading ? (
              <div className="py-20 flex items-center justify-center text-gray-500 gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Fetching live from Google Ads…</span>
              </div>
            ) : campaigns.length === 0 ? (
              <div className="py-16 text-center text-sm text-gray-500">
                {error ? 'No data.' : 'No Performance Max campaigns found for this brand.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <div className="max-h-[360px] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr className="text-left text-gray-500 font-semibold">
                        <th className="py-2 px-2 w-8"></th>
                        <th className="py-2 px-3">Campaign</th>
                        <SortTh label="Spend" sortKey="spend" />
                        <SortTh label="Revenue" sortKey="revenue" />
                        <SortTh label="Conv." sortKey="purchases" />
                        <SortTh label="Clicks" sortKey="clicks" />
                        <SortTh label="Impr." sortKey="impressions" />
                        <SortTh label="CTR" sortKey="ctr" />
                        <SortTh label="CPM" sortKey="cpm" />
                        <SortTh label="ROAS" sortKey="roas" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {sortedCampaigns.map((c) => {
                        const cKey = `${c.accountId}-${c.id}`;
                        const isOpen = !!expanded[c.id];
                        const cpm = cpmOf(c.cost, c.impressions);
                        const ctr = ctrOf(c.clicks, c.impressions);
                        const roas = roasOf(c.cost, c.conversionValue);
                        return (
                          <React.Fragment key={cKey}>
                            <tr
                              className="hover:bg-gray-50 cursor-pointer"
                              onClick={() => setExpanded(p => ({ ...p, [c.id]: !p[c.id] }))}
                            >
                              <td className="py-2 px-2 text-center">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setExpanded(p => ({ ...p, [c.id]: !p[c.id] }));
                                  }}
                                  className="p-1 rounded hover:bg-gray-200"
                                >
                                  {isOpen
                                    ? <ChevronDown className="w-4 h-4 text-gray-600" />
                                    : <ChevronRight className="w-4 h-4 text-gray-600" />}
                                </button>
                              </td>
                              <td className="py-2 px-3 text-gray-800 font-medium truncate">
                                {c.name}
                                {c.status !== 'ENABLED' && (
                                  <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${STATUS_STYLES[c.status] || 'bg-gray-100 text-gray-500'}`}>
                                    {c.status}
                                  </span>
                                )}
                                {data?.accounts?.length > 1 && (
                                  <span className="block text-[10px] text-gray-400">{c.accountName || c.accountId}</span>
                                )}
                              </td>
                              <td className="py-2 px-3 text-right text-gray-700">{money(c.cost)}</td>
                              <td className="py-2 px-3 text-right text-gray-700">{money(c.conversionValue)}</td>
                              <td className="py-2 px-3 text-right text-gray-700">{count(c.conversions)}</td>
                              <td className="py-2 px-3 text-right text-gray-700">{count(c.clicks)}</td>
                              <td className="py-2 px-3 text-right text-gray-700">{count(c.impressions)}</td>
                              <td className="py-2 px-3 text-right text-gray-700">{ctr.toFixed(2)}%</td>
                              <td className="py-2 px-3 text-right text-gray-700">{cpm > 0 ? `₺${cpm.toFixed(2)}` : '-'}</td>
                              <td className="py-2 px-3 text-right text-gray-700">{roas.toFixed(2)}x</td>
                            </tr>

                            {isOpen && (c.assetGroups || []).map((g) => {
                              const isSel = g.id === selectedGroupId;
                              const cpmG = cpmOf(g.cost, g.impressions);
                              const ctrG = ctrOf(g.clicks, g.impressions);
                              const roasG = roasOf(g.cost, g.conversionValue);
                              return (
                                <tr
                                  key={g.id}
                                  onClick={() => setSelectedGroupId(g.id)}
                                  className={`bg-gray-50 hover:bg-blue-50 cursor-pointer ${isSel ? 'bg-blue-50/60' : ''}`}
                                >
                                  <td className="py-1.5 px-2 text-center"></td>
                                  <td className="py-1.5 px-3 text-gray-700 truncate">
                                    <span className={isSel ? 'font-medium text-blue-700' : 'font-medium'}>{g.name}</span>
                                    <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded ${STATUS_STYLES[g.primaryStatus] || 'bg-gray-100 text-gray-500'}`}>
                                      {g.primaryStatus}
                                    </span>
                                  </td>
                                  <td className="py-1.5 px-3 text-right text-gray-700">{money(g.cost)}</td>
                                  <td className="py-1.5 px-3 text-right text-gray-700">{money(g.conversionValue)}</td>
                                  <td className="py-1.5 px-3 text-right text-gray-700">{count(g.conversions)}</td>
                                  <td className="py-1.5 px-3 text-right text-gray-700">{count(g.clicks)}</td>
                                  <td className="py-1.5 px-3 text-right text-gray-700">{count(g.impressions)}</td>
                                  <td className="py-1.5 px-3 text-right text-gray-700">{ctrG.toFixed(2)}%</td>
                                  <td className="py-1.5 px-3 text-right text-gray-700">{cpmG > 0 ? `₺${cpmG.toFixed(2)}` : '-'}</td>
                                  <td className="py-1.5 px-3 text-right text-gray-700">{roasG.toFixed(2)}x</td>
                                </tr>
                              );
                            })}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </Card>

          {/* PMax'te asset basina metrik yok — kullanici "neden ROAS kolonu asset'te yok?"
              diye sormasin diye acikca yaziyoruz. */}
          <div className="mt-4 flex items-start gap-2 text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-xl p-3">
            <Info className="w-4 h-4 shrink-0 mt-0.5 text-gray-400" />
            <span>
              Metrics match the Meta Creative Performance page (Spend / Revenue / Conv. / Clicks /
              Impr. / CTR / CPM / ROAS). Note that Google does <strong>not</strong> report per-asset spend
              or ROAS for Performance Max — figures are at asset group level, while asset cards show
              type, dimensions and approval status.
            </span>
          </div>
        </div>

        {/* Sağ: seçili asset grubunun asset'leri */}
        <div className="w-full lg:w-5/12">
          <Card className="rounded-xl overflow-hidden sticky top-4">
            <div className="p-4 border-b bg-white">
              {selectedGroup ? (
                <>
                  <p className="text-xs text-gray-500">{selectedGroup.campaignName}</p>
                  <div className="flex items-center justify-between mt-0.5">
                    <span className="font-semibold text-gray-900">{selectedGroup.name}</span>
                    <span className="text-sm text-gray-500">{bucketed.total} assets</span>
                  </div>
                </>
              ) : (
                <span className="font-medium text-gray-800">Asset Preview</span>
              )}
            </div>

            {!selectedGroup ? (
              <div className="py-16 text-center text-sm text-gray-500">
                Select an asset group on the left.
              </div>
            ) : bucketed.total === 0 ? (
              <div className="py-16 text-center text-sm text-gray-500">
                No assets to show in this group.
                {!includeRemoved && ' Tick “Removed assets” to include removed ones.'}
              </div>
            ) : (
              <div className="p-4 space-y-6 max-h-[70vh] overflow-y-auto">
                {/* Görseller */}
                {bucketed.images.length > 0 && (
                  <section>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <ImageIcon className="w-3.5 h-3.5" /> Images ({bucketed.images.length})
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {bucketed.images.map((a) => (
                        <div key={a.assetId} className="border border-gray-100 rounded-xl overflow-hidden bg-gray-50">
                          <div className="aspect-square bg-white flex items-center justify-center overflow-hidden">
                            <img
                              src={a.imageUrl}
                              alt={a.name || a.fieldType}
                              className="max-w-full max-h-full object-contain"
                            />
                          </div>
                          <div className="px-2.5 py-2 bg-white border-t border-gray-100">
                            <p className="text-[11px] font-medium text-gray-700 truncate" title={FIELD_LABELS[a.fieldType] || a.fieldType}>
                              {FIELD_LABELS[a.fieldType] || a.fieldType}
                            </p>
                            <div className="flex items-center justify-between mt-0.5">
                              <span className="text-[10px] text-gray-400">
                                {a.width && a.height ? `${a.width}×${a.height}` : '—'}
                              </span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${STATUS_STYLES[a.primaryStatus] || 'bg-gray-100 text-gray-500'}`}>
                                {a.primaryStatus}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Videolar */}
                {bucketed.videos.length > 0 && (
                  <section>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <Play className="w-3.5 h-3.5" /> Videos ({bucketed.videos.length})
                    </h3>
                    <div className="space-y-2">
                      {bucketed.videos.map((a) => (
                        <a
                          key={a.assetId}
                          href={`https://www.youtube.com/watch?v=${a.youtubeVideoId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex gap-3 border border-gray-100 rounded-xl p-2 hover:bg-gray-50 transition-colors"
                        >
                          <img
                            src={`https://i.ytimg.com/vi/${a.youtubeVideoId}/mqdefault.jpg`}
                            alt={a.youtubeVideoTitle || 'video'}
                            className="w-28 h-16 object-cover rounded-lg bg-gray-100 shrink-0"
                          />
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-gray-800 line-clamp-2">
                              {a.youtubeVideoTitle || 'Untitled video'}
                            </p>
                            <p className="text-[10px] text-gray-400 mt-1 flex items-center gap-1">
                              {a.youtubeVideoId} <ExternalLink className="w-3 h-3" />
                            </p>
                          </div>
                        </a>
                      ))}
                    </div>
                  </section>
                )}

                {/* Metinler */}
                {Object.entries(bucketed.textGroups).map(([field, list]) => (
                  <section key={field}>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <Type className="w-3.5 h-3.5" /> {FIELD_LABELS[field] || field} ({list.length})
                    </h3>
                    <div className="space-y-1.5">
                      {list.map((a) => (
                        <div key={a.assetId} className="flex items-start gap-2 border border-gray-100 rounded-lg px-3 py-2">
                          <span className="text-sm text-gray-700 flex-1">{a.text}</span>
                          <span className="text-[10px] text-gray-400 shrink-0 mt-0.5">{(a.text || '').length}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                ))}

                {/* Diğer (CTA vb.) */}
                {bucketed.other.length > 0 && (
                  <section>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                      <MousePointerClick className="w-3.5 h-3.5" /> Other ({bucketed.other.length})
                    </h3>
                    <div className="space-y-1.5">
                      {bucketed.other.map((a) => (
                        <div key={a.assetId} className="flex items-center justify-between border border-gray-100 rounded-lg px-3 py-2">
                          <span className="text-xs text-gray-600">{FIELD_LABELS[a.fieldType] || a.fieldType}</span>
                          <span className="text-xs text-gray-800">{a.callToAction || a.text || a.name || '—'}</span>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </Card>
        </div>
      </div>

      {/* ── Alt özet ───────────────────────────────────────────────────────── */}
      {!loading && campaigns.length > 0 && (
        <Card className="rounded-xl p-4 flex items-center justify-between">
          <span className="text-sm font-medium text-blue-600">Asset List</span>
          <span className="text-sm text-blue-600">
            Total: {Number(data?.totalAssets || 0).toLocaleString('tr-TR')} assets
          </span>
        </Card>
      )}
    </div>
  );
}
